import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Button, MenuItem, TextField } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ListDialog from 'components/popups/ListDialog';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import Error from 'components/utils/Alert/Error';
import { SUBSCRIPTION_TYPE } from 'const';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { ShopContext } from 'Context/ShopContext';
import AlertHelper from 'helpers/AlertHelper';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import StockReportService from 'services/StockReportService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { saveAs } from 'file-saver';
import { Paper, Pagination, Grid, TablePagination, Tooltip, IconButton } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function StockReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const location = useLocation();

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [viewData, setViewData] = useState('');
  const [errorMsg, setErrorMsg] = useState();
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [count, setCount] = useState(0);
  const [closingStockFilter, setClosingStockFilter] = useState('all');
  const [isEnableSearchRefreshButton, setIsEnableSearchRefreshButton] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);

  const { startDate: initialStart, endDate: initialEnd } = dynamicDateRange('STOCK_REPORT');
  const [fromDate, setFromDate] = useState(initialStart);
  const [toDate, setToDate] = useState(initialEnd);
  const [prevFilterData, setPrevFilterData] = useState({
    fromDate: initialStart,
    toDate: initialEnd,
    closingStockFilter: 'all',
  });

  const subscriptionType = shop?.subscription?.type;

  const CSV_COLUMNS = {
    'item.plu': {
      title: tCommon('PLU'),
      getValue: (item) => item?.item?.plu,
    },
    'item.name': {
      title: tCommon('Item Name'),
      getValue: (item) => item?.item?.name,
    },
    opening_stock: tCommon('Opening Stock'),
    inbound: tCommon('Inbound'),
    outbound: tCommon('Outbound'),
    closing_stock: tCommon('Closing Stock'),
    stock_value: {
      title: tCommon('Stock Value'),
      getValue: (item) => ShopsHelper.getAmountFormatted(shop, item?.item?.cost * item?.item?.stock),
    },
    stock_sales_value: {
      title: tCommon('Stock Value'),
      getValue: (item) => ShopsHelper.getAmountFormatted(shop, item?.item?.price * item?.item?.stock),
    },
  };

  const ONE_PRODUCT_CSV_COLUMNS = {
    type: tCommon('Type'),
    date: {
      title: tCommon('Date'),
      getValue: (item) => {
        return item.created_at ? DateTimeHelpers.getDate(item.created_at) : '';
      },
    },
    time: {
      title: tCommon('Time'),
      getValue: (item) => {
        return item.created_at ? DateTimeHelpers.getTime(item.created_at) : '';
      },
    },
    description: tCommon('Details'),
    inbound: tCommon('Inbound'),
    outbound: tCommon('Outbound'),
    balance: tCommon('Balance'),
  };

  const loadStockReports = useCallback(
    async ({
      _fromDate = prevFilterData.fromDate,
      _toDate = prevFilterData.toDate,
      _offset = offset,
      _limit = limit,
      _closingStockFilter = prevFilterData.closingStockFilter,
      searchOverride,
    } = {}) => {
      setLoadingIndicator(true);
      try {
        const from = _fromDate;
        const to = _toDate;
        const data = {
          date_start: DateTimeHelpers.getDateYMD(from),
          date_end: DateTimeHelpers.getDateYMD(to),
        };
        const resp = await StockReportService.getStockReport(data, {
          offset: _offset,
          limit: _limit,
          search: searchOverride !== undefined ? searchOverride : filterText,
          closing_stock: _closingStockFilter,
        });

        const fetchedData = resp?.data || [];
        const sortedData = sortByAsending(fetchedData);

        setStocks(sortedData);
        setCount(resp?.count);
        setIsEnableSearchRefreshButton(false);
      } catch (err) {
        AlertHelper.setShortMessage(setErrorMsg, err.message);
      }
      setLoadingIndicator(false);
    },
    // eslint-disable-next-line
    [filterText, offset, limit, prevFilterData]
  );

  const sortByAsending = (data) => {
    return [...data].sort((a, b) => a['item']['plu'] - b['item']['plu']);
  };

  const generateStockReport = useCallback(() => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('STOCK_REPORT');

    setFromDate(dynamicStartDate);
    setToDate(dynamicEndDate);

    loadStockReports({
      _fromDate: dynamicStartDate,
      _toDate: dynamicEndDate,
      _offset: offset,
      _limit: limit,
      _closingStockFilter: closingStockFilter,
    });

    setPage(0);
    setOffset(0);
    setIsShowGenerateButton(false);
    setPrevFilterData({
      fromDate: dynamicStartDate,
      toDate: dynamicEndDate,
      closingStockFilter: closingStockFilter,
    });
  }, [loadStockReports, limit, offset, closingStockFilter]);

  // const handleDateChange = async (from, to) => {
  //   setLoadingIndicator(true);
  //   setFromDate(from);
  //   setToDate(to);
  //   loadStockReports({ _fromDate: from, _toDate: to });
  // };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(stocks, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `STOCK-CHANGE-REPORT-${dateTime}.csv`);
    console.log(shop);

    postAlertMessage({
      text: tCommon('Exported to excel successfully'),
      type: 'success',
    });
  };

  const viewDetailsHandler = (data) => {
    if (!data || !data.transactions.length) {
      return setViewData(data);
    }

    const transactions = [];
    let balance = data.opening_stock;
    for (const transaction of data.transactions) {
      balance += transaction.quantity;
      transactions.push({ ...transaction, balance });
    }

    const upadatedData = {
      ...data,
      transactions: [
        {
          type: tCommon('Opening Stock'),
          time: '  ',
          description: '',
          quantity: ' ',
          balance: data.opening_stock,
        },
        ...transactions,
        {
          type: tCommon('Closing Stock'),
          time: '  ',
          description: '',
          quantity: ' ',
          balance: data.closing_stock,
        },
      ],
    };

    setViewData(upadatedData);
  };

  const handleCloseDetailsDialog = () => {
    setViewData(null);
  };

  const headerData = [
    {
      label: tCommon('PLU'),
      id: 'item.plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'item.name',
      type: 'text',
    },
    {
      label: tCommon('Opening Stock'),
      id: 'opening_stock',
      type: 'floatQuantity',
    },
    {
      label: tCommon('Inbound'),
      id: 'inbound',
      type: 'floatAmount',
    },
    {
      label: tCommon('Outbound'),
      id: 'outbound',
      type: 'floatAmount',
    },
    {
      label: tCommon('Closing Stock'),
      id: 'closing_stock',
      type: 'floatQuantity',
    },
    {
      label: tCommon('Stock Value'),
      id: 'stock_value',
      type: 'callback',
      viewRender: (entry) => {
        return ShopsHelper.getAmountFormatted(shop, entry.closing_stock * entry.item.cost);
      },
    },
    {
      label: tCommon('Stock Sales Value'),
      id: 'stock_sales_value',
      type: 'callback',
      viewRender: (entry) => {
        return ShopsHelper.getAmountFormatted(shop, entry.closing_stock * entry.item.price);
      },
    },
    {
      label: tCommon('Actions'),
      id: 'btnViewDetail',
      type: 'callback',
      viewRender: (item) => (
        <Tooltip title="View Details">
          <IconButton onClick={() => viewDetailsHandler(item)}>
            <RemoveRedEyeIcon className={styles.iconButton} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];
  const transactionHeader = [
    {
      label: tCommon('Type'),
      id: 'type',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'Date',
      type: 'callback',
      viewRender: (item) => {
        return item.created_at ? DateTimeHelpers.getDate(item.created_at) : '';
      },
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => {
        return item.created_at ? DateTimeHelpers.convertTo12HourTime(item.created_at) : '';
      },
    },
    {
      label: tCommon('Details'),
      id: 'description',
      type: 'text',
    },
    {
      label: tCommon('Inbound'),
      id: 'inbound',
      type: 'callback',
      viewRender: (item) => (item.quantity && item.quantity > 0 ? item.quantity : '-'),
    },
    {
      label: tCommon('Outbound'),
      id: 'outbound',
      type: 'callback',
      viewRender: (item) => (item.quantity && item.quantity < 0 ? -1 * item.quantity : '-'),
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'floatAmount',
    },
  ];

  const handleChangePage = (event, page) => {
    const newPage = page - 1;
    setPage(newPage);
    let offset = newPage * limit;
    setOffset(offset);
    loadStockReports({ _offset: offset });
  };

  const handleChangeRowsPerPage = (event) => {
    const _limit = +event.target.value;
    setLimit(_limit);
    loadStockReports({ _limit });
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    const prevFrom = prevFilterData?.fromDate ? new Date(prevFilterData.fromDate).valueOf() : null;
    const prevTo = prevFilterData?.toDate ? new Date(prevFilterData.toDate).valueOf() : null;
    const curFrom = fromDate ? new Date(fromDate).valueOf() : null;
    const curTo = toDate ? new Date(toDate).valueOf() : null;

    return curFrom !== prevFrom || curTo !== prevTo || closingStockFilter !== prevFilterData.closingStockFilter;
  }, [fromDate, toDate, prevFilterData, closingStockFilter]);

  useEffect(() => {
    if (isShowGenerateButton === null) return;

    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [fromDate, toDate, isShowGenerateButton, prevFilterData, isAnyChangeOnReportsFilters]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const itemNameParam = params.get('itemName');
    const { startDate, endDate } = dynamicDateRange('STOCK_REPORT');

    setOffset(0);
    setPage(0);

    if (itemNameParam) {
      setFilterText(itemNameParam);

      const from = prevFilterData?.fromDate ?? startDate;
      const to = prevFilterData?.toDate ?? endDate;

      loadStockReports({
        _fromDate: from,
        _toDate: to,
        _offset: 0,
        searchOverride: itemNameParam,
      });

      setPrevFilterData({ fromDate: from, toDate: to, closingStockFilter: 'all' });
      setIsShowGenerateButton(false);
    } else {
      setFromDate(startDate);
      setToDate(endDate);
      setClosingStockFilter('all');
      setPrevFilterData({ fromDate: startDate, toDate: endDate, closingStockFilter: 'all' });
      loadStockReports({ _fromDate: startDate, _toDate: endDate, _offset: 0, _closingStockFilter: 'all' });
      setIsShowGenerateButton(false);
    }
    // eslint-disable-next-line
  }, [location.search]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Stock')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>

        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Export As')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tCommon('CSV')}
            </Button>
          </div>
        )}
      </div>
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo title={tCommon('Feature not available')} content={tCommon('No stock report feature')} />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tReport('Stock Report from {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(fromDate),
                    to: DateTimeHelpers.convertDateToDMY(toDate),
                  })}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={filterText}
                    onChange={(e) => {
                      setFilterText(e.target.value);
                      setIsEnableSearchRefreshButton(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setOffset(0);
                        setPage(0);
                        loadStockReports({ _offset: 0 });
                      }
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search items')}
                  />
                  <Tooltip title={tCommon('Refresh')}>
                    <IconButton
                      className={styles.iconBtn}
                      onClick={() => {
                        setOffset(0);
                        setPage(0);
                        loadStockReports({ _offset: 0 });
                      }}
                      disabled={!isEnableSearchRefreshButton}
                    >
                      <SearchIcon className={styles.searchIcon} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange setFilterFromDate={setFromDate} setFilterToDate={setToDate} storageKey="STOCK_REPORT" />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Closing Stock')}</label>
                </div>
                <div>
                  <TextField
                    select
                    name="closingStockFilter"
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ width: 135 }}
                    value={closingStockFilter}
                    onChange={(event) => {
                      setClosingStockFilter(event.target.value);
                    }}
                  >
                    <MenuItem value={'all'}>{tCommon('All')}</MenuItem>
                    <MenuItem value={'negative'}>{tCommon('Negative')}</MenuItem>
                    <MenuItem value={'zero'}>{tCommon('Zero')}</MenuItem>
                    <MenuItem value={'positive'}>{tCommon('Positive')}</MenuItem>
                  </TextField>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Generate Reports')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateStockReport}
                    disabled={isShowGenerateButton === false}
                  >
                    <SyncIcon className={styles.actionBtnIcon} />
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}
          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : (stocks && stocks.length) || loadingIndicator ? (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable columns={headerData} rows={stocks || []} rowKey="item.id" isLoading={loadingIndicator} />
              </div>
              <Grid container>
                <Grid item xs={6} display="flex" justifyContent="start">
                  <TablePagination
                    rowsPerPageOptions={[20, 50]}
                    component="div"
                    count={count}
                    rowsPerPage={limit}
                    page={page}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      '& .MuiTablePagination-actions': {
                        display: 'none',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="end" pt={1}>
                  <Pagination
                    count={Math.ceil(count / limit)}
                    page={page + 1}
                    color="primary"
                    onChange={handleChangePage}
                    defaultPage={1}
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : (
            !loadingIndicator && (
              <Info
                title={tReport('You have no stock to list')}
                content={tReport("You haven't any stock to list with given properties")}
              />
            )
          )}
          {viewData && (
            <ListDialog
              onClose={handleCloseDetailsDialog}
              toggleItemsTab={viewDetailsHandler}
              tableHeader={transactionHeader}
              rowData={viewData.transactions}
              title={
                viewData
                  ? tReport('Stock details of {{item_name}}', { item_name: viewData.item.name })
                  : tReport('stock details')
              }
              emptyDataContent={tCommon('No transaction found for this item')}
              csvColumns={ONE_PRODUCT_CSV_COLUMNS}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(StockReport);
